import { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const PROJECT_TYPES = ["Kitchen Cabinets", "Bathroom Vanity", "Built-Ins", "Millwork", "Furniture", "Other"];

const initial = { name: "", email: "", phone: "", project_type: "", message: "" };

export function Contact() {
  const [form, setForm] = useState(initial);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and a short message.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setDone(true);
      setForm(initial);
      toast.success("Thanks — we'll be in touch shortly.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not send. Try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const inputCls =
    "w-full bg-transparent border-b border-cream/25 focus:border-terracotta outline-none py-3 font-sans text-cream placeholder:text-cream/40 transition-colors duration-300";

  return (
    <section id="contact" data-testid="contact-section" className="relative bg-charcoal py-24 md:py-32 overflow-hidden">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-16">
        <div className="lg:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <span className="h-px w-12 bg-terracotta" />
            <span className="font-sans uppercase tracking-[0.3em] text-xs text-cream/50">Get in touch</span>
          </div>
          <h2 className="font-serif text-cream text-5xl md:text-6xl tracking-tight leading-[0.95]">
            Let's build <span className="italic text-terracotta">something</span>.
          </h2>
          <p className="font-sans text-cream/60 leading-relaxed mt-8 max-w-sm">
            Tell us about your project. We'll follow up to schedule a consultation.
            Proudly serving Camas, Vancouver, Washougal, Battle Ground and all of Oregon.
          </p>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              data-testid="contact-success"
              className="border border-cream/20 p-12 h-full flex flex-col justify-center"
            >
              <p className="font-serif italic text-terracotta text-4xl mb-4">Message sent.</p>
              <p className="font-sans text-cream/70">
                Thank you for reaching out — we'll respond within a couple of business days.
              </p>
              <button
                onClick={() => setDone(false)}
                data-testid="contact-reset"
                className="mt-8 self-start font-sans uppercase tracking-[0.2em] text-xs text-cream border-b border-cream/40 pb-1 hover:text-terracotta transition-colors"
              >
                Send another
              </button>
            </motion.div>
          ) : (
            <form onSubmit={submit} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <input data-testid="contact-name" className={inputCls} placeholder="Full name *" value={form.name} onChange={set("name")} />
                <input data-testid="contact-email" type="email" className={inputCls} placeholder="Email *" value={form.email} onChange={set("email")} />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <input data-testid="contact-phone" className={inputCls} placeholder="Phone" value={form.phone} onChange={set("phone")} />
                <select data-testid="contact-project-type" className={`${inputCls} appearance-none`} value={form.project_type} onChange={set("project_type")}>
                  <option value="" className="bg-charcoal">Project type</option>
                  {PROJECT_TYPES.map((t) => (
                    <option key={t} value={t} className="bg-charcoal">{t}</option>
                  ))}
                </select>
              </div>
              <textarea data-testid="contact-message" rows={4} className={`${inputCls} resize-none`} placeholder="Tell us about your project *" value={form.message} onChange={set("message")} />
              <button
                type="submit"
                disabled={submitting}
                data-testid="contact-submit"
                className="group inline-flex items-center gap-3 bg-terracotta text-cream px-10 py-4 font-sans uppercase tracking-[0.2em] text-xs hover:bg-cream hover:text-charcoal transition-colors duration-300 disabled:opacity-60"
              >
                {submitting ? "Sending…" : "Send message"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}
